// A "•••" actions menu: the WAI-ARIA menu button. The trigger opens a
// usePopover panel of menuitems; focus moves to the first item, ArrowUp/Down,
// Home/End move, Enter or Space runs one and closes, Escape closes and hands
// focus back. It is how the project header, and later the board's cards and
// columns, offer actions without hover or drag (spec §6.17).
import { useRef, type KeyboardEvent } from 'react';
import { usePopover } from './dialog';
import { Icon, type IconName } from './primitives';

export type MenuItem = {
  label: string;
  onSelect: () => void;
  icon?: IconName;
  danger?: boolean;
  disabled?: boolean;
};

export function Menu({
  label,
  items,
  align = 'right',
}: {
  /** The trigger's name ("Project actions"). */
  label: string;
  items: MenuItem[];
  align?: 'left' | 'right';
}) {
  const pop = usePopover({ align, maxH: 360, minWidth: 200 });
  const listRef = useRef<HTMLDivElement | null>(null);
  const focusItem = (i: number) => {
    const all = listRef.current?.querySelectorAll<HTMLButtonElement>(
      '[role="menuitem"]:not(:disabled)',
    );
    if (!all?.length) return;
    all[(i + all.length) % all.length]?.focus();
  };
  const open = () => {
    pop.show();
    requestAnimationFrame(() => focusItem(0));
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const all = [
      ...(listRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]:not(:disabled)',
      ) ?? []),
    ];
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    const to: Record<string, number> = {
      ArrowDown: at + 1,
      ArrowUp: at - 1,
      Home: 0,
      End: all.length - 1,
    };
    if (e.key in to) {
      e.preventDefault();
      focusItem(to[e.key]);
      return;
    }
    if (e.key === 'Tab') pop.close(false);
    pop.panelProps.onKeyDown(e);
  };
  if (items.length === 0) return null;
  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        {...pop.triggerProps}
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        onClick={() => (pop.open ? pop.close() : open())}
        onKeyDown={(e) => {
          if (!pop.open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            open();
            return;
          }
          pop.triggerProps.onKeyDown(e);
        }}
        className="hf-btn hf-hit"
        data-variant="ghost"
        style={{
          width: 32,
          height: 32,
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 'var(--hf-radius-md)',
        }}
      >
        <Icon name="more" size={16} />
      </button>
      {pop.open && (
        <div
          {...pop.panelProps}
          ref={(el) => {
            listRef.current = el;
            pop.panelProps.ref(el);
          }}
          role="menu"
          aria-label={label}
          onKeyDown={onKeyDown}
          style={{
            ...pop.panelProps.style,
            overflowY: 'auto',
            padding: 'var(--hf-space-1)',
            borderRadius: 'var(--hf-radius-card)',
            background: 'var(--hf-paper-2)',
            color: 'var(--hf-ink)',
            border: '1px solid var(--hf-rule-2)',
            boxShadow: 'var(--hf-shadow-pop)',
          }}
        >
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              disabled={it.disabled}
              tabIndex={-1}
              onClick={() => {
                pop.close();
                it.onSelect();
              }}
              className="hf-row-link"
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--hf-space-2)',
                padding: 'var(--hf-space-2) var(--hf-space-3)',
                borderRadius: 'var(--hf-radius-md)',
                border: 0,
                background: 'transparent',
                cursor: it.disabled ? 'default' : 'pointer',
                whiteSpace: 'nowrap',
                textAlign: 'left',
                fontFamily: 'inherit',
                fontSize: 'var(--hf-text-sm)',
                color: it.disabled
                  ? 'var(--hf-ink-3)'
                  : it.danger
                    ? 'var(--hf-rose-text)'
                    : 'var(--hf-ink-2)',
              }}
            >
              {it.icon && <Icon name={it.icon} size={14} />}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
